/**
 * AdminUserUsageTable Component
 *
 * Sortable, searchable table showing users ranked by token usage
 * with pagination. Each row is clickable to drill into user details.
 */

import React from 'react';
import { ArrowUpDown, ArrowDown, AlertCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { AdminUserUsageSummary } from '../../types/tokenUsage';

export type SortField = 'total_tokens' | 'total_cost' | 'api_call_count' | 'username';

export interface AdminUserUsageTableProps {
  users: AdminUserUsageSummary[];
  total: number;
  isLoading: boolean;
  error?: string | null;
  onUserClick: (user: AdminUserUsageSummary) => void;
  sortBy: SortField;
  onSortChange: (field: SortField) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const formatNumber = (n: number): string => n.toLocaleString('en-US');

const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

const formatRelativeTime = (isoDate: string | null): string => {
  if (!isoDate) return 'Never';
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-6" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-14" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" /></td>
    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" /></td>
  </tr>
);

const SortIcon: React.FC<{ field: SortField; currentSort: SortField }> = ({ field, currentSort }) => {
  if (field !== currentSort) {
    return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
  }
  return <ArrowDown className="w-3.5 h-3.5 ml-1 text-primary-600" />;
};

const AdminUserUsageTable: React.FC<AdminUserUsageTableProps> = ({
  users,
  total,
  isLoading,
  error = null,
  onUserClick,
  sortBy,
  onSortChange,
  searchQuery,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const maxTokens = users.length > 0 ? Math.max(...users.map(u => u.total_tokens)) : 0;

  if (error) {
    return (
      <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center">
        <AlertCircle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
        <span className="text-red-700 dark:text-red-400">{error}</span>
      </div>
    );
  }

  const sortableHeader = (label: string, field: SortField) => (
    <button
      className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      onClick={() => onSortChange(field)}
    >
      {label}
      <SortIcon field={field} currentSort={sortBy} />
    </button>
  );

  // Calculate rank offset based on current page
  const rankOffset = (currentPage - 1) * 20;

  return (
    <div data-testid="admin-user-usage-table" className="bg-white dark:bg-cap-dark rounded-lg shadow overflow-hidden">
      {/* Header with title and search */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
            User Rankings
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({total} {total === 1 ? 'user' : 'users'})
            </span>
          </h3>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              aria-label="Search users"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="input pl-10 w-full text-sm"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
              <th className="px-4 py-3 text-left">{sortableHeader('Username', 'username')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-right">{sortableHeader('Tokens', 'total_tokens')}</th>
              <th className="px-4 py-3 text-right">{sortableHeader('Cost', 'total_cost')}</th>
              <th className="px-4 py-3 text-right">{sortableHeader('API Calls', 'api_call_count')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Active</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36">Usage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? `No users found matching "${searchQuery}" for the selected period.`
                    : 'No token usage data found for the selected period.'}
                </td>
              </tr>
            ) : (
              users.map((user, index) => {
                const barWidth = maxTokens > 0 ? (user.total_tokens / maxTokens) * 100 : 0;
                return (
                  <tr
                    key={user.user_id}
                    onClick={() => onUserClick(user)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    data-testid={`user-row-${user.user_id}`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-medium">
                      {rankOffset + index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-warm-white">
                      {user.username}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono text-gray-900 dark:text-warm-white">
                      {formatNumber(user.total_tokens)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono text-gray-900 dark:text-warm-white">
                      {formatCurrency(user.total_cost)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono text-gray-900 dark:text-warm-white">
                      {formatNumber(user.api_call_count)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500 dark:text-gray-400">
                      {formatRelativeTime(user.last_active)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                          {user.percentage_of_total.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {rankOffset + 1}-{Math.min(rankOffset + users.length, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || isLoading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[80px] text-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoading}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserUsageTable;
